import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check, Loader2, Heart, Compass, Sparkles, Camera, ScrollText, MapPin, Star, Quote, BadgeCheck, Clock, ShieldCheck, Info } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { holySites, prayerPlans } from '../mockData';
import { useToast } from '../hooks/use-toast';
// import DeliveryProofStrip from '../components/DeliveryProofStrip'; // Kept on disk — re-enable when ready
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import WaitlistDialog from '../components/WaitlistDialog';
import CheckoutPausedBanner from '../components/CheckoutPausedBanner';
import { getCheckoutEnabled, useCheckoutEnabled } from '../lib/checkoutStatus';

const PHOTO_PROOF_PRICE = 15;

// One-time tiers — we round UP to the nearest tier for any selection
const oneTimeTiers = [
  { maxSites: 1, price: 29, name: 'Community Prayer' },
  { maxSites: 3, price: 49, name: 'Sacred Journey' },
  { maxSites: 5, price: 79, name: 'Complete Blessing' },
];

const getOneTimeTier = (count) => {
  if (count <= 1) return oneTimeTiers[0];
  if (count <= 3) return oneTimeTiers[1];
  return oneTimeTiers[2];
};

// Get the best subscription plan for a site count
const getBestSubPlan = (count) => {
  if (count <= 1) return 'foundation';
  if (count <= 3) return 'devotion';
  return 'sanctuary';
};

const prayerCategories = {
  healing: { icon: Heart, color: 'from-rose-500 to-pink-600', bgColor: 'bg-rose-50', borderColor: 'border-rose-300', textColor: 'text-rose-700' },
  guidance: { icon: Compass, color: 'from-blue-500 to-indigo-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-300', textColor: 'text-blue-700' },
  blessing: { icon: Sparkles, color: 'from-amber-500 to-orange-600', bgColor: 'bg-amber-50', borderColor: 'border-amber-300', textColor: 'text-amber-700' }
};

const PrayerRequest = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const category = searchParams.get('category');

  const [step, setStep] = useState(1);
  const [prayerType, setPrayerType] = useState('one-time'); // Pre-select One-Time (safer default for cold traffic)
  const [selectedSites, setSelectedSites] = useState(() => {
    const id = Number(searchParams.get('site'));
    return holySites.some((s) => s.id === id) ? [id] : [];
  });
  const [prayerText, setPrayerText] = useState('');

  // Pre-fill prayer from homepage, library, or abandoned-cart recovery.
  // Auto-skip to step 2 only when the prayer looks like an abandoned-cart
  // recovery (user already typed theirs). For library handoffs we LAND on
  // step 1 so the customer can see and personalise the template.
  useEffect(() => {
    const prayerParam = searchParams.get('prayer');
    const sourceParam = searchParams.get('source');
    if (prayerParam) {
      setPrayerText(prayerParam);
      if (prayerParam.length >= 50 && sourceParam !== 'library') {
        setStep(2);
      }
    }
  }, []);
  const [dedicatedTo, setDedicatedTo] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const checkoutEnabled = useCheckoutEnabled();
  const [prayerCategory, setPrayerCategory] = useState(category || null);
  const [wantsPhotoProof, setWantsPhotoProof] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [scrollPhotoIndex, setScrollPhotoIndex] = useState(0);
  const [testimonyIndex, setTestimonyIndex] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef(null);
  const prayerTextareaRef = useRef(null);
  const [featuredReviewIndex, setFeaturedReviewIndex] = useState(0);
  // Prayer Request ID — generated the moment the user completes Step 1 so the
  // customer feels psychological ownership of the prayer BEFORE seeing pricing.
  // Format: JHP-YYYY-Wxx-XXXX (year, ISO-week, 4-char hash).
  const [prayerRequestId, setPrayerRequestId] = useState('');
  // Best-guess default country from the browser locale (e.g., "en-CA" -> "CA").
  // Refined by IP geolocation on mount (see useEffect below).
  const [defaultCountry, setDefaultCountry] = useState(() => {
    try {
      const loc = (navigator.language || 'en-US').toUpperCase();
      const parts = loc.split('-');
      if (parts.length >= 2 && parts[1].length === 2) return parts[1];
    } catch (_) {}
    return 'US';
  });
  const [errors, setErrors] = useState({});
  const { toast } = useToast();
  const { t } = useLanguage();

  // Clear a field error as the user starts fixing it
  const clearError = (field) => {
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Validate the current step and return a map of errors
  const validateStep = (s) => {
    const errs = {};
    if (s === 1) {
      if (!name.trim()) errs.name = 'Please enter your name';
      if (!dedicatedTo.trim()) errs.dedicatedTo = 'Please tell us who this prayer is for';
      if (!email.trim() || !email.includes('@')) errs.email = 'Please enter a valid email';
      // Phone is required so we can follow up on abandoned prayers.
      // - Must be in international E.164 format (starts with + and NOT +0)
      // - At least 8 digits after stripping non-digits (shortest real international
      //   numbers e.g. Denmark 8-digit + country code = 10 chars).
      // This catches the common bug where the country flag is wrong (US default)
      // and users type their local number, producing malformed outputs like
      // `+0876...` (Irish local typed under US flag).
      const raw = (phone || '').trim();
      const digitCount = raw.replace(/\D/g, '').length;
      if (!raw || digitCount < 8) {
        errs.phone = 'Please enter a valid phone number';
      } else if (!raw.startsWith('+')) {
        errs.phone = 'Tap the flag first, then enter your number';
      } else if (raw.startsWith('+0')) {
        errs.phone = 'Please tap the flag to select your country, then enter the number without the leading 0';
      }
      if (prayerText.trim().length < 20) errs.prayerText = 'Please write your prayer (at least 20 characters)';
    } else if (s === 2) {
      if (selectedSites.length < 1) errs.sites = 'Please select at least one holy site';
    } else if (s === 3) {
      if (prayerType === 'journey' && !selectedPlan) errs.plan = 'Please choose a plan to continue';
      if (!prayerType) errs.prayerType = 'Please choose one-time or weekly journey';
    }
    return errs;
  };

  // Scroll to the first invalid field and focus its input
  const scrollToFirstError = (errs) => {
    const firstKey = Object.keys(errs)[0];
    if (!firstKey) return;
    setTimeout(() => {
      const el = document.querySelector(`[data-error-field="${firstKey}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = el.querySelector('input, textarea');
        if (input) {
          try { input.focus({ preventScroll: true }); } catch (_) { input.focus(); }
        }
      }
    }, 80);
  };

  // Fire Facebook Pixel events safely (only once per session per event type)
  const trackFB = (event, params = {}) => {
    try {
      if (typeof window.fbq !== 'function') return;
      const key = `fbq_${event}`;
      if (sessionStorage.getItem(key)) return; // dedupe per session
      window.fbq('track', event, params);
      sessionStorage.setItem(key, '1');
    } catch (_) {
      // never break UX for tracking
    }
  };

  const handleNext = () => {
    const errs = validateStep(step);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast({
        title: 'Almost there',
        description: Object.values(errs)[0],
        variant: 'destructive',
      });
      scrollToFirstError(errs);
      return;
    }
    setErrors({});

    // Mid-funnel Facebook Pixel events — feed the algorithm enough data.
    // step 1 -> 2: Lead (they wrote a prayer + email)
    // step 2 -> 3: AddToCart (they picked holy sites)
    if (step === 1) {
      // Generate the customer-facing Prayer Request ID now — before pricing.
      // Psychological "commitment device": once they have an ID they feel they
      // already own the prayer and abandoning feels like leaving something behind.
      if (!prayerRequestId) {
        const now = new Date();
        const year = now.getFullYear();
        // ISO week number
        const d = new Date(Date.UTC(year, now.getMonth(), now.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
        const hash = Math.random().toString(36).slice(2, 6).toUpperCase();
        setPrayerRequestId(`JHP-${year}-W${String(weekNo).padStart(2, '0')}-${hash}`);
      }
      trackFB('Lead', {
        content_name: 'Prayer Written',
        content_category: prayerCategory || 'general',
      });
    } else if (step === 2) {
      trackFB('AddToCart', {
        content_name: `${selectedSites.length} Holy Site${selectedSites.length > 1 ? 's' : ''}`,
        num_items: selectedSites.length,
      });
    }

    setStep(step + 1);
  };

  const handleFinalSubmit = () => {
    const errs = validateStep(step);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast({
        title: 'Almost there',
        description: Object.values(errs)[0],
        variant: 'destructive',
      });
      scrollToFirstError(errs);
      return;
    }
    setErrors({});
    handleSubmit();
  };

  // Auto-rotate the scroll photo through 4 images (base + 3 new) every 3.5s
  useEffect(() => {
    const id = setInterval(() => {
      setScrollPhotoIndex((i) => (i + 1) % 4);
    }, 3500);
    return () => clearInterval(id);
  }, []);

  // Auto-rotate the sacred testimony carousel every 7s (calm, devotional pace).
  // Total count = real reviews + 3 featured testimonies always shown as fillers.
  useEffect(() => {
    const total = (reviews.length || 0) + 3;
    const id = setInterval(() => {
      setTestimonyIndex((i) => (i + 1) % total);
    }, 7000);
    return () => clearInterval(id);
  }, [reviews.length]);

  // Fetch admin-approved reviews so real customer voices show up on this page
  // (falls back to nothing if there are no approved reviews yet — better than fake).
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    if (!API_URL) return;
    fetch(`${API_URL}/api/reviews/public?limit=12`)
      .then((r) => (r.ok ? r.json() : { reviews: [] }))
      .then((d) => setReviews(Array.isArray(d.reviews) ? d.reviews : []))
      .catch(() => setReviews([]));
  }, []);

  // Auto-rotate the featured review carousel under the textbox every 6 seconds.
  // Skip if only 0–1 reviews (nothing to rotate).
  useEffect(() => {
    if (reviews.length < 2) return;
    const interval = setInterval(() => {
      setFeaturedReviewIndex((i) => (i + 1) % reviews.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [reviews.length]);

  // Refine the default phone country from IP geolocation.
  // `ipwho.is` is CORS-friendly (unlike ipapi.co which blocks browser requests
  // from most production domains). Non-blocking; if it fails, the browser-locale
  // fallback set in useState still applies.
  useEffect(() => {
    let cancelled = false;
    fetch('https://ipwho.is/', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d && d.success !== false && typeof d.country_code === 'string' && d.country_code.length === 2) {
          setDefaultCountry(d.country_code.toUpperCase());
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Pause + mute the trust video when it scrolls out of view so the audio
  // doesn't keep bothering the user while they read testimonies or fill the
  // form below. When it scrolls back into view we DO NOT auto-resume audio —
  // the user must tap again — but muted autoplay is allowed to continue so
  // the visual stays alive as a trust signal.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || typeof IntersectionObserver === 'undefined') return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            // Out of view — silence it immediately
            try { v.pause(); } catch (_) { /* ignore */ }
            v.muted = true;
            if (videoPlaying) setVideoPlaying(false);
          } else if (v.paused && v.muted) {
            // Back in view — resume silent playback only (no audio)
            const p = v.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, [videoPlaying]);

  // Pre-fill from URL params (e.g. lead follow-up emails) and/or load draft
  // (abandoned cart emails). URL params are applied immediately so the form
  // looks personalized even when no draft exists; if a draft IS found, it
  // overrides because it has more complete info (prayer_text, dedicated_to…).
  useEffect(() => {
    const nameParam = searchParams.get('name');
    const emailParam = searchParams.get('email');
    if (nameParam) setName(nameParam);
    if (emailParam) setEmail(emailParam);

    if (emailParam) {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      fetch(`${API_URL}/api/prayer/draft/${encodeURIComponent(emailParam)}`)
        .then(res => res.json())
        .then(data => {
          if (data.found) {
            if (data.name) setName(data.name);
            if (data.email) setEmail(data.email);
            if (data.prayer_text) setPrayerText(data.prayer_text);
            if (data.dedicated_to) setDedicatedTo(data.dedicated_to);
            if (data.category) setPrayerCategory(data.category);
          }
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    // Scroll to the step content area on every step change, accounting for header.
    // While orders are paused, keep step 1 at the top so the pause notice is seen first.
    setTimeout(async () => {
      if (step === 1 && !(await getCheckoutEnabled())) return;
      const stepIds = {
        1: 'step-write-prayer',
        2: 'step-choose-sites',
        3: 'step-choose-plan'
      };
      const el = document.querySelector(`[data-testid="${stepIds[step]}"]`);
      if (el) {
        const headerOffset = 100;
        const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: elementPosition - headerOffset, behavior: 'smooth' });
      }
    }, 200);
  }, [step]);

  useEffect(() => {
    if (category && prayerCategories[category]) {
      setPrayerCategory(category);
    }
  }, [category]);

  const toggleSite = (siteId) => {
    if (selectedSites.includes(siteId)) {
      setSelectedSites(selectedSites.filter(id => id !== siteId));
    } else {
      setSelectedSites([...selectedSites, siteId]);
    }
  };

  // Auto-save draft — single consolidated save
  const draftSavedRef = React.useRef(false);
  const draftUpdatedRef = React.useRef(false);
  
  // Save draft once when moving past Step 1
  useEffect(() => {
    if (step >= 2 && !draftSavedRef.current) {
      draftSavedRef.current = true;
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const draftEmail = email.trim() && email.includes('@') && !email.endsWith('@draft') ? email : `anonymous-${Date.now()}@draft`;
      fetch(`${API_URL}/api/prayer/save-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prayer_text: prayerText,
          selected_sites: [],
          prayer_type: 'unknown',
          dedicated_to: dedicatedTo,
          name: name,
          email: draftEmail,
          phone: phone,
          category: prayerCategory
        })
      }).catch(() => {});
    }
  }, [step]);

  // Update draft with sites when reaching Step 3
  useEffect(() => {
    if (step >= 3 && draftSavedRef.current && !draftUpdatedRef.current) {
      draftUpdatedRef.current = true;
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const siteNames = selectedSites.map(id => {
        const site = holySites.find(s => s.id === id);
        return site ? site.name : String(id);
      });
      const draftEmail = email.trim() && email.includes('@') && !email.endsWith('@draft') ? email : `anonymous-${Date.now()}@draft`;
      fetch(`${API_URL}/api/prayer/save-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prayer_text: prayerText,
          selected_sites: siteNames,
          prayer_type: prayerType || 'unknown',
          dedicated_to: dedicatedTo,
          name: name,
          email: draftEmail,
          phone: phone,
          category: prayerCategory
        })
      }).catch(() => {});
    }
  }, [step]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (!(await getCheckoutEnabled())) {
      setIsSubmitting(false);
      setWaitlistOpen(true);
      return;
    }
    try {
      const tier = getOneTimeTier(selectedSites.length);
      const holySiteNames = selectedSites.map(id => {
        const site = holySites.find(s => s.id === id);
        return site ? site.name : String(id);
      });

      const API_URL = process.env.REACT_APP_BACKEND_URL;

      // Save draft (fire and forget) — keeps abandoned-cart flow intact
      fetch(`${API_URL}/api/prayer/save-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prayer_text: prayerText,
          selected_sites: holySiteNames,
          prayer_type: prayerType,
          dedicated_to: dedicatedTo,
          name: name,
          email: email,
          phone: phone,
          category: prayerCategory
        })
      }).then(() => {
        fetch(`${API_URL}/api/prayer/mark-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, prayer_text: prayerText })
        }).catch(() => {});
      }).catch(() => {});

      // Track Facebook Pixel: InitiateCheckout
      if (typeof window.fbq === 'function') {
        const checkoutValue = prayerType === 'community' ? 9
                            : prayerType === 'one-time' ? tier.price
                            : 0;
        const checkoutName = prayerType === 'community' ? 'Community Blessing'
                           : prayerType === 'one-time' ? tier.name
                           : selectedPlan;
        window.fbq('track', 'InitiateCheckout', {
          value: checkoutValue,
          currency: 'USD',
          content_name: checkoutName
        });
      }

      // Store prayer data so /checkout/success can retrieve it if needed
      localStorage.setItem('pendingPrayer', JSON.stringify({
        prayer_type: prayerType,
        selected_plan: prayerType === 'journey' ? selectedPlan : tier.name,
        selected_sites: holySiteNames,
        prayer_text: prayerText,
        dedicated_to: dedicatedTo,
        name,
        email,
        category: prayerCategory,
        wants_photo_proof: wantsPhotoProof,
      }));

      // Build Stripe checkout session body — goes DIRECTLY to Stripe.
      // (previously we routed through /checkout — that intermediate page was
      // causing conversion drop-off, per Clarity session recordings)
      const requestBody = {
        origin_url: window.location.origin,
        email: email,
        prayer_type: prayerType || 'general',
        dedicated_to: dedicatedTo,
        prayer_text: prayerText,
        holy_sites_names: holySiteNames,
      };
      if (prayerType === 'community') {
        // Community Blessing — no sites, no photo proof, flat $9. Backend
        // recognises prayer_type === 'community' and prices accordingly.
      } else if (prayerType === 'one-time') {
        requestBody.sites = tier.maxSites;
        requestBody.photo_proof = !!wantsPhotoProof;
      } else {
        requestBody.plan_id = selectedPlan;
        requestBody.billing_period = 'monthly';
      }

      const res = await fetch(`${API_URL}/api/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      const data = await res.json();

      if (!res.ok || !data.checkout_url) {
        let msg = 'Failed to create checkout session';
        if (typeof data.detail === 'string') msg = data.detail;
        throw new Error(msg);
      }

      // Fire AddPaymentInfo — high-signal event for Facebook optimization
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'AddPaymentInfo', {
          value: data.amount || 0,
          currency: (data.currency || 'usd').toUpperCase(),
        });
      }

      // Straight to Stripe. No intermediate page.
      window.location.replace(data.checkout_url);
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: error.message || "Failed to process your request. Please try again.",
        variant: "destructive"
      });
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return prayerText.trim().length >= 20 && name.trim() && email.trim() && email.includes('@') && dedicatedTo.trim();
      case 2: return selectedSites.length >= 1;
      case 3:
        if (prayerType === 'one-time') return true;
        if (prayerType === 'journey') return selectedPlan !== null;
        return false;
      default: return true;
    }
  };

  const stepLabels = ['Prayer', 'Sites', 'Plan'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 sm:pt-24 pb-16 overflow-x-hidden">
      <SEO
        title="Online Prayer Request — Send Your Prayer to Jerusalem"
        description="Submit your online prayer request and we will physically carry it to Jerusalem's holy sites — the Church of the Holy Sepulchre, Western Wall, Via Dolorosa, and Garden Tomb. Photo proof of delivery included."
        path="/prayer-request"
      />
      <WaitlistDialog
        open={waitlistOpen}
        onOpenChange={setWaitlistOpen}
        defaults={{ name, email, prayer_text: prayerText, dedicated_to: dedicatedTo }}
        source="prayer-request"
        interest={prayerType || ''}
      />
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <CheckoutPausedBanner className="mb-8 mt-4 sm:mt-0" onJoin={() => setWaitlistOpen(true)} />

          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full blur-xl opacity-30 animate-pulse"></div>
                <div className="relative rounded-full bg-gradient-to-br from-amber-500 via-amber-600 to-amber-700 shadow-2xl ring-4 ring-amber-200 overflow-hidden">
                  <img
                    src="/logo-circle.png"
                    alt="Jerusalem Holy Prayer"
                    className="h-20 w-20 object-cover brightness-0 invert"
                  />
                </div>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 font-serif" data-testid="prayer-request-title">
              {t('prayerRequest.title')}
            </h1>
            <p className="text-xl text-gray-600">{t('prayerRequest.subtitle')}</p>
          </div>

          {/* Prayer Category Banner */}
          {prayerCategory && prayerCategories[prayerCategory] && (
            <div className={`mb-8 p-4 md:p-6 rounded-2xl ${prayerCategories[prayerCategory].bgColor} border-2 ${prayerCategories[prayerCategory].borderColor} shadow-lg`}>
              <div className="flex items-center justify-center gap-3 md:gap-4">
                <div className={`p-2.5 md:p-3 rounded-full bg-gradient-to-br ${prayerCategories[prayerCategory].color} shadow-lg`}>
                  {React.createElement(prayerCategories[prayerCategory].icon, { className: "w-6 h-6 md:w-8 md:h-8 text-white" })}
                </div>
                <div className="text-center flex-1">
                  <h2 className={`text-xl md:text-2xl font-bold ${prayerCategories[prayerCategory].textColor} font-serif`}>
                    Prayer for {prayerCategory.charAt(0).toUpperCase() + prayerCategory.slice(1)}
                  </h2>
                  <p className="text-gray-600 mt-1 text-sm md:text-base">
                    {prayerCategory === 'healing' && 'Seek restoration and wholeness through sacred prayer'}
                    {prayerCategory === 'guidance' && 'Find wisdom and direction for life\'s decisions'}
                    {prayerCategory === 'blessing' && 'Invite divine favor and grace into your life'}
                  </p>
                </div>
                <button onClick={() => setPrayerCategory(null)} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Clear category">
                  <span className="text-xl">&times;</span>
                </button>
              </div>
            </div>
          )}

          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex items-center justify-between max-w-3xl mx-auto">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold transition-all text-sm sm:text-base ${
                      s < step ? 'bg-green-500 text-white' :
                      s === step ? 'bg-amber-600 text-white' :
                      'bg-gray-200 text-gray-500'
                    }`}>
                      {s < step ? <Check size={20} /> : s}
                    </div>
                    <span className="text-xs mt-2 font-medium text-gray-600 hidden sm:block">
                      {stepLabels[s - 1]}
                    </span>
                  </div>
                  {s < 3 && (
                    <div className={`flex-1 h-1 mx-2 sm:mx-4 ${s < step ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <Card className="border-2 border-gray-200 shadow-xl pb-20 sm:pb-0">
            <CardContent className="p-4 sm:p-8">

              {/* ======== STEP 1: Write Your Prayer ======== */}
              {step === 1 && (
                <div className="space-y-6" data-testid="step-write-prayer">
                  {/* Reverent Sunday delivery note — informational, not pressuring */}
                  <div
                    data-testid="scroll-departure-note"
                    className="flex items-center justify-center gap-2 text-gray-500 text-sm py-1"
                  >
                    <Clock className="w-3.5 h-3.5 text-amber-600/70" strokeWidth={2} />
                    <p className="italic">
                      Our next scroll is prepared for Sunday&apos;s journey to Jerusalem.
                    </p>
                  </div>

                  <div className="text-center mb-2">
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 font-serif">{t('prayerRequest.writePrayer')}</h2>
                    <p className="text-gray-500 mt-2">Your prayer will be printed on a scroll and delivered by hand to Jerusalem&apos;s holy sites.</p>
                    <p className="text-[11px] text-gray-400 mt-1.5 italic">A physical delivery service — we carry and photograph your prayer, we do not pray on your behalf.</p>
                  </div>

                  {/* ========== PRIMARY ACTION: textarea + name/email + pills ========== */}
                  {/* Moved here so cold ad visitors land directly on the writing zone */}
                  <div data-error-field="prayerText">
                    <Textarea
                      ref={prayerTextareaRef}
                      value={prayerText}
                      onChange={(e) => { setPrayerText(e.target.value); clearError('prayerText'); }}
                      onFocus={(e) => {
                        if (typeof window.gtag === 'function') {
                          window.gtag('event', 'prayer_text_focus', { event_category: 'engagement' });
                        }
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 300);
                      }}
                      placeholder="Dear Lord, please watch over my daughter Sarah as she starts her new job..."
                      className={`min-h-56 sm:min-h-72 text-base sm:text-lg border-2 focus:border-amber-600 placeholder:text-gray-300 ${errors.prayerText ? 'border-red-500 ring-1 ring-red-300' : 'border-gray-300'}`}
                      data-testid="prayer-text-input"
                    />
                    {errors.prayerText && <p className="text-xs text-red-600 mt-1" data-testid="step1-prayer-error">{errors.prayerText}</p>}
                    <div className="mt-1.5 flex justify-between items-center text-xs text-gray-400">
                      <span>Be honest. Be specific.</span>
                      <span>{prayerText.length} / 1500</span>
                    </div>
                  </div>

                  {/* Inspiration pills — quick starters if they don't know what to write */}
                  {!prayerText && (
                    <div className="flex flex-wrap gap-2 justify-center">
                      <span className="text-xs text-gray-400">Need inspiration?</span>
                      {[
                        { label: "Healing", text: "Dear Lord, I ask for Your healing touch upon my loved ones. Please restore their health and give them strength." },
                        { label: "Family", text: "Heavenly Father, please watch over my family. Protect them and bless our home with peace and love." },
                        { label: "Guidance", text: "Lord, I seek Your wisdom and guidance. Show me the path and give me peace in my heart." },
                      ].map((t) => (
                        <button
                          key={t.label}
                          onClick={() => setPrayerText(t.text)}
                          className="px-3 py-1 text-xs rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 transition-all"
                          data-testid={`inspiration-${t.label.toLowerCase()}`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Progressive-reveal — the personal fields appear ONLY once the user has typed something,
                      mirroring the prayergranted.com pattern (reduces initial friction) */}
                  <div
                    className={`transition-all duration-500 ${prayerText.length > 0 ? 'opacity-100 max-h-[600px]' : 'opacity-0 max-h-0 overflow-hidden'}`}
                    aria-hidden={prayerText.length === 0}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div data-error-field="name">
                        <label className="text-sm text-gray-600 font-medium block mb-1">Your Name</label>
                        <Input
                          value={name}
                          onChange={(e) => { setName(e.target.value); clearError('name'); }}
                          placeholder="Your full name"
                          className={`focus:border-amber-600 ${errors.name ? 'border-red-500 ring-1 ring-red-300' : 'border-gray-300'}`}
                          data-testid="step1-name-input"
                        />
                        {errors.name && <p className="text-xs text-red-600 mt-1" data-testid="step1-name-error">{errors.name}</p>}
                      </div>
                      <div data-error-field="dedicatedTo">
                        <label className="text-sm text-gray-600 font-medium block mb-1">Who is this prayer for?</label>
                        <Input
                          value={dedicatedTo}
                          onChange={(e) => { setDedicatedTo(e.target.value); clearError('dedicatedTo'); }}
                          placeholder="e.g., My mother Sarah"
                          className={`focus:border-amber-600 ${errors.dedicatedTo ? 'border-red-500 ring-1 ring-red-300' : 'border-gray-300'}`}
                          data-testid="step1-dedicated-input"
                        />
                        {errors.dedicatedTo && <p className="text-xs text-red-600 mt-1" data-testid="step1-dedicated-error">{errors.dedicatedTo}</p>}
                      </div>
                      <div data-error-field="email">
                        <label className="text-sm text-gray-600 font-medium block mb-1">Your Email</label>
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => { setEmail(e.target.value); clearError('email'); }}
                          placeholder="Where we'll send proof"
                          className={`focus:border-amber-600 ${errors.email ? 'border-red-500 ring-1 ring-red-300' : 'border-gray-300'}`}
                          data-testid="step1-email-input"
                        />
                        {errors.email && <p className="text-xs text-red-600 mt-1" data-testid="step1-email-error">{errors.email}</p>}
                      </div>
                    </div>
                    {/* Phone (REQUIRED) — international phone input with country flag
                        auto-detected from browser locale + IP. Stores in E.164 format
                        (+15551234567) so admin can dial/WhatsApp directly. */}
                    <div className="mt-3" data-error-field="phone">
                      <label className="text-sm text-gray-600 font-medium block mb-1">
                        Phone number
                      </label>
                      <div className={`phone-input-wrapper rounded-md border ${errors.phone ? 'border-red-500 ring-1 ring-red-300' : 'border-gray-300 focus-within:border-amber-600 focus-within:ring-1 focus-within:ring-amber-500/40'} bg-white px-2 py-1.5 flex items-center gap-2`}>
                        <PhoneInput
                          international
                          defaultCountry={defaultCountry}
                          value={phone}
                          onChange={(v) => { setPhone(v || ''); clearError('phone'); }}
                          placeholder="Enter your phone number"
                          className="w-full text-sm"
                          data-testid="step1-phone-input"
                          numberInputProps={{
                            className: 'w-full outline-none bg-transparent py-1.5 text-base md:text-sm',
                            inputMode: 'tel',
                            autoComplete: 'tel',
                          }}
                        />
                      </div>
                      {errors.phone && <p className="text-xs text-red-600 mt-1" data-testid="step1-phone-error">{errors.phone}</p>}
                      <p className="text-[11px] text-gray-500 mt-1 italic">
                        Tap the flag <span className="not-italic">👆</span> if your country is wrong. We&apos;ll only reach you about your prayer.
                      </p>
                    </div>
                    <p className="text-xs text-gray-400 mt-2 text-center italic">
                      Your prayer is 100% private — never shared publicly without your consent.
                    </p>

                    {/* ========== FEATURED REVIEWS CAROUSEL — placed right before
                        the CTA at the exact moment of decision. Auto-rotates every
                        6s through admin-approved reviews. Styled like a real
                        customer message, not a stock testimonial block. */}
                    {reviews.length > 0 && (() => {
                      const activeIndex = featuredReviewIndex % reviews.length;
                      const r = reviews[activeIndex];
                      const initial = (r.name || '?').trim().charAt(0).toUpperCase();
                      const dateStr = (() => {
                        try {
                          const d = new Date(r.created_at);
                          return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                        } catch (_) { return null; }
                      })();
                      return (
                        <div className="mt-5" data-testid="featured-review-carousel">
                          <div
                            key={r.review_id}
                            data-testid="featured-review-card"
                            className="rounded-xl bg-gradient-to-br from-amber-50/70 to-white border border-amber-200/70 shadow-sm px-4 py-4 md:px-5 md:py-5 animate-in fade-in duration-500"
                          >
                            <div className="flex items-start gap-3">
                              {/* Avatar w/ initial — feels like a real profile, no fake stock photo */}
                              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 text-white font-semibold flex items-center justify-center shadow-inner ring-2 ring-white">
                                {initial}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-semibold text-gray-900">{r.name}</span>
                                  {r.location && (
                                    <span className="text-xs text-gray-500">· {r.location}</span>
                                  )}
                                  {r.verified_purchase && (
                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-green-700 font-semibold">
                                      <BadgeCheck className="w-3 h-3" /> Verified
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-0.5 mt-0.5" aria-label={`${r.rating} out of 5 stars`}>
                                  {[1,2,3,4,5].map(n => (
                                    <Star
                                      key={n}
                                      size={13}
                                      className={n <= (r.rating || 5) ? 'text-amber-500 fill-amber-400' : 'text-gray-300'}
                                      strokeWidth={1.5}
                                    />
                                  ))}
                                  {dateStr && <span className="text-[11px] text-gray-400 ml-1.5">· {dateStr}</span>}
                                </div>
                                <p className="mt-2 text-sm md:text-[15px] text-gray-700 leading-relaxed font-serif italic">
                                  &ldquo;{r.text}&rdquo;
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Tap-to-switch dot indicators — small, unobtrusive */}
                          {reviews.length > 1 && (
                            <div className="flex items-center justify-center gap-1.5 mt-3" role="tablist" aria-label="More reviews">
                              {reviews.map((_, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => setFeaturedReviewIndex(i)}
                                  aria-label={`Show review ${i + 1}`}
                                  data-testid={`featured-review-dot-${i}`}
                                  className={`h-1.5 rounded-full transition-all ${
                                    activeIndex === i ? 'w-5 bg-amber-600' : 'w-1.5 bg-amber-200 hover:bg-amber-300'
                                  }`}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* INLINE Send button — prayergranted.com style, no sticky footer on Step 1 */}
                    <div className="mt-5">
                      <Button
                        onClick={handleNext}
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white text-base font-semibold py-6 min-h-[56px] shadow-md hover:shadow-lg transition-all"
                        data-testid="prayer-inline-send-btn"
                      >
                        Yes — Send My Prayer to Jerusalem
                        <ChevronRight className="ml-1" size={22} />
                      </Button>
                      {/* Transparent pricing reassurance — surfaces cost before Step 3 so
                          users are never surprised. Kept calm & informational (no popup). */}
                      <div className="flex items-center justify-center gap-1.5 mt-3 text-[13px] text-gray-500" data-testid="price-reassurance">
                        <span className="font-semibold text-gray-700">From $9</span>
                        <span aria-hidden="true">·</span>
                        <ShieldCheck className="w-3.5 h-3.5 text-amber-600" aria-hidden="true" />
                        <span>30-day money-back guarantee</span>
                      </div>
                      <p className="text-center text-xs text-gray-400 mt-1">
                        Next: choose your sacred sites • no charge yet
                      </p>
                    </div>
                  </div>

                  {/* ========== BELOW-FOLD REASSURANCE: trust badges, photos, pricing, testimonies, video ========== */}

                  {/* Trust reminder for ad visitors — non-clickable (fixes Clarity dead-click issue) */}
                  <div className="grid grid-cols-3 gap-2 text-center pt-4 border-t border-amber-100 pointer-events-none select-none">
                    <div className="bg-amber-50/60 rounded-lg p-3">
                      <ScrollText className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                      <p className="text-xs text-gray-700 font-medium">Printed on a Scroll</p>
                    </div>
                    <div className="bg-amber-50/60 rounded-lg p-3">
                      <MapPin className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                      <p className="text-xs text-gray-700 font-medium">Delivered to Jerusalem</p>
                    </div>
                    <div className="bg-amber-50/60 rounded-lg p-3">
                      <Camera className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                      <p className="text-xs text-gray-700 font-medium">Photo Proof Sent</p>
                    </div>
                  </div>

                  {/* Auto-playing silent Jerusalem delivery video — replaces the static
                      photo carousel so skeptics see real motion without needing to tap.
                      Muted + loop + playsInline are required for iOS Safari autoplay.
                      Poster loads first (fast LCP), video streams behind it. */}
                  {(() => {
                    return (
                      <div data-testid="prayer-scroll-photo" className="relative mx-auto max-w-[420px] md:max-w-[460px] rounded-xl overflow-hidden shadow-md ring-1 ring-amber-200/60 bg-black">
                        <video
                          ref={(el) => { videoRef.current = el; window.__scrollVideo = el; }}
                          src="/video/jerusalem-scroll-journey.mp4"
                          poster="/video/jerusalem-scroll-poster.jpg"
                          autoPlay
                          muted
                          playsInline
                          preload="metadata"
                          controls={videoPlaying}
                          onEnded={() => {
                            // Stop pestering the user once playback finishes —
                            // mute, reset controls, and hide the "playing" UI.
                            const v = videoRef.current;
                            if (v) { v.muted = true; }
                            const engaged = videoPlaying; // true only if user tapped "sound"
                            setVideoPlaying(false);

                            // If the user actually watched with sound on, gently
                            // guide them back up to the prayer textarea — that's
                            // the next action we want them to take. No focus (would
                            // pop mobile keyboard); just a smooth scroll into view.
                            if (engaged && prayerTextareaRef.current) {
                              setTimeout(() => {
                                try {
                                  prayerTextareaRef.current.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                  });
                                } catch (_) { /* ignore */ }
                                if (typeof window.gtag === 'function') {
                                  window.gtag('event', 'video_ended_scroll_to_prayer', { event_category: 'engagement' });
                                }
                              }, 400);
                            }
                          }}
                          className="w-full aspect-[3/4] object-cover object-bottom block bg-black"
                          data-testid="hero-video-player"
                        />

                        {/* Live-red pulse + label — matches the "real, hand-delivered" trust signal */}
                        {!videoPlaying && (
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-3 py-2 pointer-events-none">
                            <p className="text-xs md:text-sm text-white/95 font-medium tracking-wide flex items-center gap-1.5">
                              <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-400"></span>
                              </span>
                              <span>Photographed at the Church of the Holy Sepulchre, Jerusalem</span>
                            </p>
                          </div>
                        )}

                        {/* "Tap for sound" pill — top-right, small, unobtrusive.
                            On tap we un-mute and enable native controls. */}
                        {!videoPlaying && (
                          <button
                            type="button"
                            onClick={() => {
                              // Must un-mute imperatively inside the click handler — modern
                              // browsers only allow un-muting as a direct response to a user
                              // gesture. Setting state alone (muted={...}) is too indirect.
                              const v = videoRef.current || window.__scrollVideo;
                              if (v) {
                                v.muted = false;
                                v.currentTime = 0;
                                const p = v.play();
                                if (p && typeof p.catch === 'function') { p.catch(() => {}); }
                              }
                              setVideoPlaying(true);
                            }}
                            className="absolute top-2 right-2 inline-flex items-center gap-1.5 pl-2 pr-3 py-1.5 rounded-full bg-white/95 hover:bg-white shadow-lg text-xs font-semibold text-amber-800 backdrop-blur-sm transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-amber-500"
                            aria-label="Tap for sound"
                            data-testid="play-video-btn"
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 8v8a4.5 4.5 0 0 0 2.5-4z"/>
                            </svg>
                            Tap for sound
                          </button>
                        )}
                      </div>
                    );
                  })()}

                  {/* Emergency rush callout — above standard pricing. For cold
                      traffic in crisis who need delivery tomorrow, not next Sunday. */}
                  <a
                    href="/urgent"
                    data-testid="prayer-request-urgent-callout"
                    className="group block rounded-xl border border-red-200 bg-gradient-to-r from-red-50 to-amber-50 hover:from-red-100 hover:to-amber-100 transition-all shadow-sm hover:shadow-md"
                  >
                    <div className="flex items-center gap-3 px-4 py-3 md:px-5 md:py-3.5">
                      <div className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full bg-red-600 flex items-center justify-center shadow">
                        <Clock className="w-5 h-5 md:w-5 md:h-5 text-white" aria-hidden="true" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm md:text-base text-gray-900 leading-tight">
                          Need it by <span className="text-red-700">tomorrow</span>? Rush delivery available
                        </p>
                        <p className="text-xs md:text-sm text-gray-600 leading-snug mt-0.5">
                          $150 · Next business day · Photo + video from the holy site →
                        </p>
                      </div>
                    </div>
                  </a>

                  {/* Elegant inline pricing — 3 tiers in one row, discreet but visible */}
                  <div data-testid="prayer-pricing-strip" className="rounded-xl border border-amber-200/70 bg-gradient-to-b from-amber-50/40 to-white px-4 pt-6 pb-4 md:px-6 md:pt-7 md:pb-5">
                    <div className="flex items-center justify-center gap-5 md:gap-10">
                      <div className="text-center" data-testid="pricing-inline-1">
                        <div className="text-xl md:text-3xl font-serif font-bold text-gray-900 leading-none">$29</div>
                        <div className="text-xs md:text-sm text-gray-500 mt-1.5 whitespace-nowrap">1 Holy Site</div>
                      </div>
                      <span className="text-amber-300 text-xl md:text-2xl" aria-hidden="true">·</span>
                      <div className="text-center relative" data-testid="pricing-inline-3">
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-1.5 py-px bg-amber-600 text-white text-[7px] md:text-[8px] font-bold uppercase tracking-wider rounded-full whitespace-nowrap z-10 shadow-sm">
                          Popular
                        </div>
                        <div className="text-xl md:text-3xl font-serif font-bold text-amber-700 leading-none">$49</div>
                        <div className="text-xs md:text-sm text-gray-600 mt-1.5 font-medium whitespace-nowrap">3 Holy Sites</div>
                      </div>
                      <span className="text-amber-300 text-xl md:text-2xl" aria-hidden="true">·</span>
                      <div className="text-center" data-testid="pricing-inline-5">
                        <div className="text-xl md:text-3xl font-serif font-bold text-gray-900 leading-none">$79</div>
                        <div className="text-xs md:text-sm text-gray-500 mt-1.5 whitespace-nowrap">5 Holy Sites</div>
                      </div>
                    </div>
                    <p className="text-center text-base md:text-lg text-gray-700 mt-4 leading-relaxed">
                      Your contribution covers the sacred journey: printing, hand-delivery, and photo proof from the Holy Land
                    </p>
                    <p className="text-center text-sm md:text-base text-gray-600 mt-3 italic">
                      One-time offering · 30-day money-back guarantee
                    </p>
                  </div>

                  {/* Holy sites visualizer — "This is what you're paying for."
                      Tiny square thumbnails of every site the customer can choose. Rich
                      product proof + reinforces why the pricing feels fair. */}
                  <div data-testid="holy-sites-visualizer" className="rounded-xl border border-amber-200/60 bg-white p-4 md:p-5 shadow-sm">
                    <p className="text-center text-[13px] md:text-sm font-semibold uppercase tracking-wider text-amber-800 mb-1">Your prayer is delivered to</p>
                    <p className="text-center text-xs text-gray-500 mb-4">Choose any combination when you order</p>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                      {[
                        { src: '/proof/prayer-page-scroll.jpeg', name: 'Holy Sepulchre' },
                        { src: '/proof/holy-sepulchre-interior.jpg', name: 'Sepulchre Interior' },
                        { src: '/proof/abbey-of-dormition.jpg', name: 'Abbey of Dormition' },
                        { src: '/proof/stone-of-anointing.jpg', name: 'Stone of Anointing' },
                        { src: holySites[2]?.image, name: holySites[2]?.name || 'Church of All Nations' },
                        { src: holySites[5]?.image || holySites[4]?.image, name: holySites[5]?.name || holySites[4]?.name || 'Western Wall' },
                      ].map((s, i) => (
                        <div key={i} className="flex flex-col items-center gap-1.5">
                          <div className="relative w-full aspect-square rounded-lg overflow-hidden shadow-sm ring-1 ring-amber-100 bg-stone-100">
                            {s.src ? (
                              <img
                                src={s.src}
                                alt={s.name}
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-amber-50">
                                <MapPin className="w-4 h-4 text-amber-500" />
                              </div>
                            )}
                          </div>
                          <p className="text-[10px] md:text-[11px] text-center text-gray-700 font-medium leading-tight line-clamp-2 min-h-[26px]">{s.name}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-center gap-1.5 mt-4 pt-3 border-t border-amber-100">
                      <ShieldCheck className="w-3.5 h-3.5 text-amber-600" aria-hidden="true" />
                      <p className="text-[11px] md:text-xs text-gray-600">Every site verified with dated photo proof</p>
                    </div>
                  </div>

                  {/* Social proof nudge */}
                  <p className="text-center text-xs text-amber-700 font-semibold">Join believers from Canada, UK, Australia and around the world sending prayers to Jerusalem today</p>

                  {/* Compact photo proof strip — temporarily hidden. To re-enable: uncomment the import at the top and the <DeliveryProofStrip /> here. */}
                  {/* <DeliveryProofStrip /> */}




                  {/* Testimony carousel — shows admin-approved real reviews only.
                      Hidden entirely when we have zero reviews so the page never
                      shows fake or "sample" testimonies. */}
                  {reviews.length > 0 && (() => {
                    const all = reviews;
                    const active = all[testimonyIndex % all.length] || all[0];
                    return (
                      <div
                        data-testid="testimony-carousel"
                        className="max-w-xl mx-auto text-center px-2 pt-6 mt-4"
                      >
                        <div className="w-10 h-px bg-amber-300 mx-auto mb-5" aria-hidden="true" />
                        <div key={testimonyIndex} className="min-h-[140px] md:min-h-[110px] animate-in fade-in duration-700">
                          <div className="flex items-center justify-center gap-0.5 mb-3" aria-label={`${active.rating} out of 5 stars`}>
                            {[1,2,3,4,5].map(n => (
                              <Star
                                key={n}
                                size={16}
                                className={n <= (active.rating || 5) ? 'text-amber-500 fill-amber-400' : 'text-gray-300'}
                                strokeWidth={1.5}
                              />
                            ))}
                          </div>
                          <p className="font-serif italic text-base md:text-lg text-gray-700 leading-relaxed">
                            &ldquo;{active.text}&rdquo;
                          </p>
                          <p className="mt-3 text-sm text-amber-800 tracking-wide">
                            — {active.name}{active.location ? `, ${active.location}` : ''}
                            {active.verified_purchase && (
                              <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-green-700 font-semibold">
                                <BadgeCheck className="w-3 h-3" /> Verified
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="w-10 h-px bg-amber-300 mx-auto mt-5" aria-hidden="true" />

                        {/* Discreet dot indicators */}
                        {all.length > 1 && (
                          <div className="flex items-center justify-center gap-1.5 mt-4">
                            {all.map((_, i) => (
                              <button
                                key={i}
                                onClick={() => setTestimonyIndex(i)}
                                aria-label={`Show testimony ${i + 1}`}
                                data-testid={`testimony-dot-${i}`}
                                className={`h-1.5 rounded-full transition-all ${
                                  (testimonyIndex % all.length) === i ? 'w-5 bg-amber-600' : 'w-1.5 bg-amber-200 hover:bg-amber-300'
                                }`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
              {step === 2 && (
                <div className="space-y-6" data-testid="step-choose-sites">
                  <div className="text-center mb-2">
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 font-serif">Where Should Your Prayer Be Delivered?</h2>
                    <p className="text-gray-500 mt-2">Tap the sacred places that speak to your heart.</p>
                  </div>

                  {/* Gentle selection indicator */}
                  {selectedSites.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center" data-testid="site-count-indicator">
                      <p className="text-green-800 font-medium text-sm">
                        {selectedSites.length === 1
                          ? '1 sacred site chosen'
                          : `${selectedSites.length} sacred sites chosen`}
                      </p>
                    </div>
                  )}

                  {/* Compact chip grid — small photo thumbnails, 2 cols on mobile,
                      3 on desktop. Each holy site keeps its image so users have
                      an emotional anchor while browsing quickly. */}
                  <div data-error-field="sites">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {holySites.map((site) => {
                        const isSelected = selectedSites.includes(site.id);
                        return (
                          <button
                            key={site.id}
                            type="button"
                            onClick={() => { toggleSite(site.id); clearError('sites'); }}
                            data-testid={`site-card-${site.id}`}
                            className={`group relative flex flex-col items-stretch text-left rounded-2xl overflow-hidden transition-all active:scale-95 ${
                              isSelected
                                ? 'border-2 border-amber-500 shadow-lg ring-2 ring-amber-200 bg-amber-50'
                                : 'border border-gray-200 hover:border-amber-300 hover:shadow-md bg-white shadow-sm'
                            }`}
                          >
                            <div className="relative aspect-[4/3] w-full overflow-hidden bg-stone-100">
                              <img
                                src={site.image}
                                alt={site.name}
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                              {/* Subtle dark gradient for readability of name */}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" aria-hidden="true" />
                              {isSelected && (
                                <>
                                  <div className="absolute inset-0 bg-amber-600/25" aria-hidden="true" />
                                  <span className="absolute top-2 right-2 bg-amber-500 text-white rounded-full p-1 shadow-lg" aria-hidden="true">
                                    <Check size={14} strokeWidth={3} />
                                  </span>
                                </>
                              )}
                            </div>
                            <div className={`px-3 py-2.5 ${isSelected ? 'bg-amber-50' : 'bg-white'}`}>
                              <p className={`text-[13px] leading-tight font-semibold font-serif line-clamp-2 ${isSelected ? 'text-amber-900' : 'text-gray-900'}`}>
                                {site.name}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {errors.sites && <p className="text-sm text-red-600 mt-3 text-center font-medium" data-testid="step2-sites-error">{errors.sites}</p>}
                  </div>
                </div>
              )}

              {/* ======== STEP 3: How Would You Like Your Prayer Delivered? ======== */}
              {step === 3 && (
                <div className="space-y-6" data-testid="step-choose-plan">
                  {/* Prayer Request ID banner — ONLY appears when the customer has
                      chosen the Personal ($29+) or subscription tier. Community $9
                      customers do not get an ID (per the tier promise). */}
                  {prayerRequestId && prayerType && prayerType !== 'community' && (
                    <div
                      data-testid="prayer-request-id-banner"
                      className="rounded-xl bg-green-50 border border-green-200 px-4 py-3 flex items-center gap-3"
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center">
                        <Check className="w-4 h-4" strokeWidth={3} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-green-900">
                          <span className="font-semibold">Your Prayer ID:</span>
                        </p>
                        <p className="text-sm font-mono font-semibold text-green-800 truncate" data-testid="prayer-request-id">
                          {prayerRequestId}
                        </p>
                        <p className="text-[11px] text-green-700/80 mt-1 leading-snug">
                          This ID will appear on the scroll photo we send you from Jerusalem — that&apos;s how you&apos;ll know it&apos;s yours.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="text-center mb-2">
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 font-serif">Choose Your Contribution</h2>
                    <p className="text-gray-500 mt-2">
                      Carried to{' '}
                      {selectedSites.length === 1
                        ? <strong>{holySites.find(s => s.id === selectedSites[0])?.name}</strong>
                        : <strong>{selectedSites.length} sacred sites</strong>
                      }
                    </p>
                  </div>

                  {/* Sunday delivery note — reverent, informational (real weekly cutoff) */}
                  <div
                    data-testid="step3-sunday-note"
                    className="flex items-center justify-center gap-2 text-gray-500 text-sm py-1"
                  >
                    <Clock className="w-3.5 h-3.5 text-amber-600/70" strokeWidth={2} />
                    <p className="italic text-center">
                      Complete before Thursday to join this Sunday&apos;s scroll to Jerusalem.
                    </p>
                  </div>

                  {/* Sacred prayer receipt — reminds them of the value BEFORE the price */}
                  <div
                    data-testid="prayer-receipt"
                    className="bg-white border border-amber-200 rounded-2xl p-5 shadow-sm"
                  >
                    <p className="text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold text-center mb-4">
                      Your Sacred Journey
                    </p>
                    <ul className="space-y-2.5">
                      <li className="flex items-start gap-2.5">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                        <div className="flex-1 min-w-0 text-sm break-words">
                          <span className="font-semibold text-gray-800">Your prayer:</span>{' '}
                          <span className="text-gray-600 italic">
                            &ldquo;{prayerText.slice(0, 90)}{prayerText.length > 90 ? '…' : ''}&rdquo;
                          </span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                        <div className="flex-1 min-w-0 text-sm break-words">
                          <span className="font-semibold text-gray-800">Delivered to:</span>{' '}
                          <span className="text-gray-700">
                            {prayerType === 'community'
                              ? 'Included in this Sunday\u2019s Jerusalem scroll'
                              : selectedSites
                                  .map((id) => holySites.find((s) => s.id === id)?.name)
                                  .filter(Boolean)
                                  .join(', ')}
                          </span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                        <span className="text-sm text-gray-700">
                          {prayerType === 'community'
                            ? 'Confirmation email once the scroll leaves for Jerusalem'
                            : 'Photo proof of delivery emailed within 7 days'}
                        </span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                        <span className="text-sm text-gray-700">30-day money-back guarantee, no questions</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-amber-50/60 border border-amber-100 rounded-lg px-4 py-3 text-center">
                    <p className="text-sm text-gray-600 italic">
                      Your prayer is sacred. Your contribution covers the journey — the hands that carry it, and the proof it arrived.
                    </p>
                  </div>

                  {/* ========== COMMUNITY BLESSING — cheapest tier, no photo, no
                      site. Prayer is included in the weekly Sunday scroll to
                      Jerusalem. This tier exists specifically to capture people
                      who would abandon at $29. ========== */}
                  <div data-error-field="prayerType">
                  <div
                    onClick={() => { setPrayerType('community'); setSelectedPlan(null); clearError('prayerType'); clearError('plan'); }}
                    data-testid="plan-community"
                    className={`rounded-2xl p-5 md:p-6 cursor-pointer transition-all relative ${
                      prayerType === 'community'
                        ? 'bg-amber-50 border-2 border-amber-500 shadow-lg'
                        : 'bg-white border-2 border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          prayerType === 'community' ? 'border-amber-500 bg-amber-500' : 'border-gray-300'
                        }`}>
                          {prayerType === 'community' && <Check size={14} className="text-white" />}
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-base sm:text-lg font-bold text-gray-900 font-serif">Community Blessing</h3>
                          <p className="text-gray-600 mt-1 text-sm leading-relaxed">
                            Your prayer joins this Sunday&apos;s Community Scroll to Jerusalem. Our team chooses which holy sites the scroll visits each week.
                          </p>
                          {/* Subtle "no proof" line — visible without shouting */}
                          <div className="mt-2.5 flex items-start gap-1.5 text-gray-500">
                            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-[2px] text-gray-400" strokeWidth={2} />
                            <p className="text-[12px] leading-snug">
                              No photo of the scroll and no individual Prayer ID with this contribution.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-2xl font-bold text-amber-600">$9</div>
                        <div className="text-xs text-gray-500">one contribution</div>
                      </div>
                    </div>
                  </div>

                  {/* Divider — visual break between community and premium tiers */}
                  <div className="flex items-center gap-4">
                    <div className="flex-1 h-px bg-gray-200"></div>
                    <span className="text-xs text-gray-400 font-medium">or contribute more for photo proof</span>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>

                  {/* ONE-TIME — the premium tier with photo proof (existing) */}
                  <div
                    onClick={() => { setPrayerType('one-time'); setSelectedPlan(null); clearError('prayerType'); clearError('plan'); }}
                    data-testid="plan-onetime"
                    className={`rounded-2xl p-5 md:p-8 cursor-pointer transition-all ${
                      prayerType === 'one-time'
                        ? 'bg-amber-50 border-2 border-amber-500 shadow-lg'
                        : 'bg-white border-2 border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          prayerType === 'one-time' ? 'border-amber-500 bg-amber-500' : 'border-gray-300'
                        }`}>
                          {prayerType === 'one-time' && <Check size={14} className="text-white" />}
                        </div>
                        <div>
                          <h3 className="text-lg sm:text-xl font-bold text-gray-900 font-serif">One-Time Delivery</h3>
                          <p className="text-gray-600 mt-1 text-sm">
                            Delivered once to {selectedSites.length === 1 ? 'your chosen site' : `your ${selectedSites.length} chosen sites`} with photo proof.
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-2xl font-bold text-amber-600">${getOneTimeTier(selectedSites.length).price}</div>
                        <div className="text-xs text-gray-500">one contribution</div>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="flex items-center gap-4">
                    <div className="flex-1 h-px bg-gray-200"></div>
                    <span className="text-sm text-gray-400 font-medium">or keep your prayer ongoing</span>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>

                  {/* SUBSCRIPTION — shown second as a soft upsell (no badge for cold traffic) */}
                  <div
                    onClick={() => { if (prayerType !== 'journey') { setPrayerType('journey'); setSelectedPlan(getBestSubPlan(selectedSites.length)); clearError('prayerType'); clearError('plan'); } }}
                    data-testid="plan-subscription"
                    className={`rounded-2xl p-5 md:p-8 cursor-pointer transition-all relative ${
                      prayerType === 'journey'
                        ? 'bg-amber-50 border-2 border-amber-500 shadow-lg'
                        : 'bg-white border-2 border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        prayerType === 'journey' ? 'border-amber-500 bg-amber-500' : 'border-gray-300'
                      }`}>
                        {prayerType === 'journey' && <Check size={14} className="text-white" />}
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 font-serif">Ongoing Prayer Journey</h3>
                        <p className="text-gray-600 mt-1 text-sm">
                          Your prayer delivered regularly — a continuous spiritual connection to the Holy Land. Cancel anytime.
                        </p>
                      </div>
                    </div>

                    {/* Subscription Plans — stacked on mobile, 3-col on desktop */}
                    {prayerType === 'journey' && (
                      <div className="mt-4 space-y-3 md:space-y-0 md:grid md:grid-cols-3 md:gap-4" data-testid="subscription-plans">
                        {prayerPlans.map((plan) => {
                          const isRecommended = plan.id === getBestSubPlan(selectedSites.length);
                          return (
                            <div
                              key={plan.id}
                              onClick={(e) => { e.stopPropagation(); setSelectedPlan(plan.id); clearError('plan'); }}
                              data-testid={`sub-plan-${plan.id}`}
                              className={`relative rounded-xl p-4 md:p-5 cursor-pointer transition-all ${
                                selectedPlan === plan.id
                                  ? 'bg-white border-2 border-amber-500 shadow-md'
                                  : 'bg-white/80 border-2 border-gray-200 hover:border-amber-300'
                              }`}
                            >
                              {isRecommended && (
                                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                                  <span className="bg-amber-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap">BEST FOR YOU</span>
                                </div>
                              )}
                              <div className="flex md:flex-col items-center md:text-center gap-4 md:gap-1">
                                <div className="flex-1 md:flex-none">
                                  <h4 className="text-base md:text-lg font-bold text-gray-900 font-serif">{plan.name}</h4>
                                  <p className="text-gray-500 text-xs md:text-sm">{plan.description}</p>
                                </div>
                                <div className="text-xl md:text-2xl font-bold text-amber-600 md:my-2 flex-shrink-0">
                                  ${plan.price}<span className="text-xs md:text-sm text-gray-500 font-normal">/mo</span>
                                </div>
                              </div>
                              <ul className="mt-3 space-y-1.5 text-sm text-gray-600">
                                {plan.features.map((feature, idx) => (
                                  <li key={idx} className="flex items-start gap-1.5">
                                    <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Photo Proof Add-on — only for one-time */}
                  {prayerType === 'one-time' && (
                    <div>
                      <div
                        onClick={() => setWantsPhotoProof(!wantsPhotoProof)}
                        className={`cursor-pointer rounded-xl p-5 transition-all ${
                          wantsPhotoProof ? 'bg-amber-50 border-2 border-amber-400 shadow-md' : 'border-2 border-gray-200 hover:border-amber-300'
                        }`}
                        data-testid="photo-proof-addon"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                            wantsPhotoProof ? 'bg-amber-500 border-amber-500' : 'border-gray-300'
                          }`}>
                            {wantsPhotoProof && <Check size={12} className="text-white" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="flex items-center gap-2">
                                <Camera className="w-4 h-4 text-amber-600" />
                                <span className="font-semibold text-gray-900">Individual Photo Proof</span>
                              </div>
                              <span className="font-bold text-amber-600">+${PHOTO_PROOF_PRICE}</span>
                            </div>
                            <p className="text-gray-500 mt-1 text-sm">
                              A close-up photograph of <strong>your specific prayer</strong> on the scroll at the holy site.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {(errors.prayerType || errors.plan) && (
                    <p className="text-sm text-red-600 mt-2 text-center font-medium" data-testid="step3-error">
                      {errors.prayerType || errors.plan}
                    </p>
                  )}
                  </div>

                  {/* Trust & security strip — removes financial anxiety right before checkout */}
                  <div
                    data-testid="step3-trust-strip"
                    className="flex items-center justify-center flex-wrap gap-x-4 gap-y-2 py-3 border-y border-amber-100 bg-green-50/40 rounded-lg"
                  >
                    <div className="flex items-center gap-1.5 text-xs sm:text-sm text-green-800 font-medium">
                      <ShieldCheck className="w-4 h-4 text-green-600" strokeWidth={2.5} />
                      <span>30-Day Money-Back</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-700 font-medium">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      <span>{checkoutEnabled === false ? 'No payment taken while paused' : 'Secure Stripe Checkout'}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-700 font-medium">
                      <span aria-hidden="true">🇻🇦</span>
                      <span>Trusted by 40+ countries</span>
                    </div>
                  </div>

                  {/* What happens next — removes black-box anxiety */}
                  <div data-testid="what-happens-next" className="pt-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold text-center mb-4">
                      What Happens Next
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <div className="w-9 h-9 mx-auto rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center mb-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-700"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                        </div>
                        <p className="text-xs text-gray-700 leading-snug">
                          <span className="font-semibold block text-gray-900">1. Confirmation</span>
                          Instant email with your prayer receipt
                        </p>
                      </div>
                      <div className="text-center">
                        <div className="w-9 h-9 mx-auto rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center mb-2">
                          <ScrollText className="w-4 h-4 text-amber-700" strokeWidth={2} />
                        </div>
                        <p className="text-xs text-gray-700 leading-snug">
                          <span className="font-semibold block text-gray-900">2. Prepared</span>
                          Your scroll is printed for Sunday&apos;s journey
                        </p>
                      </div>
                      <div className="text-center">
                        <div className="w-9 h-9 mx-auto rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center mb-2">
                          <Camera className="w-4 h-4 text-amber-700" strokeWidth={2} />
                        </div>
                        <p className="text-xs text-gray-700 leading-snug">
                          <span className="font-semibold block text-gray-900">3. Photo proof</span>
                          Emailed to you within 7 days
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation — sticky on mobile for Steps 2 & 3 only. Step 1 uses the inline "Send My Prayer" button above. */}
              <div className={`flex justify-between mt-8 pt-6 border-t border-gray-200 gap-3 sm:relative ${step === 1 ? 'hidden sm:flex' : 'fixed'} bottom-0 left-0 right-0 bg-white sm:bg-transparent p-4 pb-6 sm:p-0 shadow-[0_-2px_10px_rgba(0,0,0,0.1)] sm:shadow-none z-50 sm:z-auto`}>
                <Button
                  variant="outline"
                  onClick={() => setStep(Math.max(1, step - 1))}
                  disabled={step === 1}
                  className="px-4 sm:px-6 py-6 sm:py-4 text-base sm:text-sm flex-1 sm:flex-none min-h-[56px] sm:min-h-0"
                  data-testid="prayer-back-btn"
                >
                  <ChevronLeft className="mr-1 sm:mr-2" size={24} />
                  <span>Back</span>
                </Button>

                {step < 3 ? (
                  <Button
                    onClick={handleNext}
                    className="bg-amber-600 hover:bg-amber-700 text-white px-4 sm:px-6 py-6 sm:py-4 text-base sm:text-sm flex-1 sm:flex-none min-h-[56px] sm:min-h-0"
                    data-testid="prayer-next-btn"
                  >
                    <span>{step === 1 ? 'Send to Jerusalem' : 'Continue'}</span>
                    <ChevronRight className="ml-1 sm:ml-2" size={24} />
                  </Button>
                ) : (
                  <Button
                    onClick={handleFinalSubmit}
                    disabled={isSubmitting}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-6 py-6 sm:py-4 text-base sm:text-sm flex-1 sm:flex-none min-h-[56px] sm:min-h-0"
                    data-testid="prayer-submit-btn"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Preparing secure payment...</>
                    ) : (
                      <>{checkoutEnabled === false ? 'Join the Waitlist' : 'Complete My Contribution'} <ChevronRight className="ml-1" size={20} /></>
                    )}
                  </Button>
                )}
              </div>
              {step === 1 && (
                <p className="hidden sm:block text-center text-xs text-gray-400 mt-3">Next: choose your holy site and delivery options</p>
              )}
              {step === 3 && (
                <>
                  <div data-testid="service-boundaries-checkout" className="mt-4 mx-4 sm:mx-0 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] leading-relaxed text-amber-900">
                    <strong>What you are purchasing:</strong> a physical delivery service. We print your written prayer, carry it to the sites you chose in Jerusalem, and email you a dated photograph as proof. We do not sell blessings, promise spiritual outcomes, or pray on your behalf.
                  </div>
                  <p className="text-center text-xs text-gray-500 mt-3 px-4 sm:px-0">
                    By clicking &ldquo;Complete My Contribution&rdquo; you agree to our{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline hover:text-amber-800">Terms of Service</a>,{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline hover:text-amber-800">Privacy Policy</a>, and{' '}
                    <a href="/refund" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline hover:text-amber-800">Refund Policy</a>.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PrayerRequest;
